'use client';

import { CategoryTabs } from '@/components/CategoryTabs';
import { ToolCard } from '@/components/ToolCard';
import { getSortedToolCategories } from '@/content/categories';
import type { Tool } from '@/content/tools';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

interface ToolsExplorerProps {
  tools: Tool[];
}

export function ToolsExplorer({ tools }: ToolsExplorerProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [activeId, setActiveId] = useState(searchParams.get('category') ?? 'all');

  const items = getSortedToolCategories().map((c) => ({
    id: c.id,
    label: c.label,
    count: tools.filter((t) => t.category === c.id).length,
  }));

  function handleSelect(id: string) {
    setActiveId(id);
    const params = new URLSearchParams(searchParams);
    if (id === 'all') {
      params.delete('category');
    } else {
      params.set('category', id);
    }
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }

  const filtered = activeId === 'all' ? tools : tools.filter((t) => t.category === activeId);

  return (
    <div>
      <CategoryTabs
        items={items}
        activeId={activeId}
        onSelect={handleSelect}
        totalCount={tools.length}
      />
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((tool) => (
          <ToolCard key={tool.slug} tool={tool} />
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="mt-8 text-center text-sm text-muted-foreground">
          No tools in this category yet — check back soon.
        </p>
      )}
    </div>
  );
}
